<template>
  <div class="container-fluid business-analytics">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <!-- Sales Pipeline: the HubSpot deals funnel -->
    <h3 class="ba-section-title">Sales Pipeline</h3>
    <sales-pipeline-section />

    <!-- Businesses -->
    <h3 class="ba-section-title">Businesses</h3>
    <div class="ba-grid">
      <analytics-chart-card
        title="Business Onboarding Trend"
        :loading="monthlyLoading"
        :error="monthlyError"
        :option="onboardingChart.option"
        :table="onboardingChart.table"
      >
        <template #info>
          <p class="mb-1">
            <strong>Approved</strong> means the Business's status first became Approved. Onboarding (Tier)
            approvals are not counted.
          </p>
          <p v-if="approvedBeforeTracking > 0" class="mb-0">
            <strong>+ {{ formatCount(approvedBeforeTracking) }} approved before tracking</strong> — Approved
            Businesses whose approval date isn't known, so they aren't counted in any month.
          </p>
        </template>
      </analytics-chart-card>
      <analytics-chart-card
        title="Month-end Business Count"
        :loading="monthlyLoading"
        :error="monthlyError"
        :option="monthEndChart.option"
        :table="monthEndChart.table"
      />
    </div>

    <!-- Business Transactions -->
    <h3 class="ba-section-title">Business Transactions</h3>
    <div class="ba-grid">
      <analytics-chart-card
        title="Transaction Status Trend"
        :loading="monthlyLoading"
        :error="monthlyError"
        :option="statusChart.option"
        :table="statusChart.table"
      >
        <template #info>
          <p class="mb-0">
            Recent months can still change as transactions finish (e.g. Processing → Recipient Paid).
            Completed = Recipient Paid; Refunded is never counted as Completed. Incomplete transactions are
            left out.
          </p>
        </template>
      </analytics-chart-card>
      <analytics-chart-card
        :title="`Business Volume (${currencyFor('volume')})`"
        :loading="monthlyLoading"
        :error="monthlyError"
        :option="volumeChart.option"
        :table="volumeChart.table"
      >
        <template #controls>
          <currency-select
            :value="currencyFor('volume')"
            :options="currencyOptions.volume"
            label="Business Volume"
            @input="selected.volume = $event"
          />
        </template>
        <template #info>
          <p class="mb-0">
            Outgoing and Incoming are never added together, and amounts in other currencies are not
            converted. A transfer between two Businesses counts on both lines: Outgoing for the sender, Incoming
            for the receiver.
          </p>
        </template>
      </analytics-chart-card>
    </div>

    <!-- Wallets -->
    <h3 class="ba-section-title">Business Wallets</h3>
    <div class="ba-position">
      <div class="ba-position-header ba-accent-header">
        <h2 class="ba-position-title">Wallet Position ({{ currencyFor("position") }})</h2>
        <currency-select
          v-if="currencyOptions.position.length"
          :value="currencyFor('position')"
          :options="currencyOptions.position"
          label="Wallet Position"
          @input="selected.position = $event"
        />
      </div>
      <div class="ba-position-body">
        <div v-if="positionLoading" class="ba-position-state">
          <b-spinner small label="Loading"></b-spinner>
          <span class="ml-2">Reading every Business wallet… this can take a minute.</span>
        </div>
        <div v-else-if="positionError" class="ba-position-state ba-state-error" role="alert">
          <i class="fa fa-exclamation-triangle mr-2" aria-hidden="true"></i>{{ positionError }}
          <button type="button" class="btn btn-sm btn-outline-secondary ml-3" @click="loadPosition">Retry</button>
        </div>
        <div v-else-if="!positionForCurrency" class="ba-position-state">
          No Business wallets hold {{ currencyFor("position") }}.
        </div>
        <template v-else>
          <div class="row">
            <div v-for="tile in positionTiles" :key="tile.label" class="col-6 col-md-3 mb-2">
              <div class="ba-tile">
                <div class="ba-tile-label">{{ tile.label }}</div>
                <div class="ba-tile-value">{{ tile.value }}</div>
              </div>
            </div>
          </div>
          <p class="ba-position-meta mb-0">
            As of {{ formatDateTime(position.fetchedAt) }}.
            <span v-if="position.businessesFailed > 0" class="ba-position-warning" role="status">
              <i class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
              Incomplete: wallets for {{ formatCount(position.businessesFailed) }} of
              {{ formatCount(position.businessesChecked) }} Businesses couldn't be read, so these totals are low.
            </span>
          </p>
        </template>
      </div>
    </div>

    <div class="ba-grid">
      <analytics-chart-card
        :title="`Wallet Deposits & Withdrawals (${currencyFor('flow')})`"
        :loading="monthlyLoading"
        :error="monthlyError"
        :option="walletFlowChart.option"
        :table="walletFlowChart.table"
      >
        <template #controls>
          <currency-select
            :value="currencyFor('flow')"
            :options="currencyOptions.flow"
            label="Wallet Deposits & Withdrawals"
            @input="selected.flow = $event"
          />
        </template>
        <template #info>
          <p class="mb-0">
            Money Businesses moved into and out of their wallets each month. Reported separately from Business
            Volume.
          </p>
        </template>
      </analytics-chart-card>
      <analytics-chart-card
        :title="`Wallet Balance Trend (${currencyFor('balance')})`"
        :loading="monthlyLoading"
        :error="monthlyError"
        empty-text="No month-end balances yet. The first one is recorded at the end of this month."
        :option="balanceChart.option"
        :table="balanceChart.table"
      >
        <template v-if="currencyOptions.balance.length" #controls>
          <currency-select
            :value="currencyFor('balance')"
            :options="currencyOptions.balance"
            label="Wallet Balance Trend"
            @input="selected.balance = $event"
          />
        </template>
        <template #info>
          <p class="mb-0">
            Balances are recorded at the end of each month, so the trend starts from the first month-end after
            tracking began<template v-if="balanceChart.trackingStarted"> ({{ balanceChart.trackingStarted }})</template>.
            <template v-if="balanceChart.hasIncomplete">
              <strong>Incomplete</strong> months had a partial month-end run, so their totals may be low.
            </template>
          </p>
        </template>
      </analytics-chart-card>
    </div>

    <!-- Business Profiles: how each company uses PayAngel -->
    <h3 class="ba-section-title">Business Profiles</h3>
    <business-profiles-card />
  </div>
</template>

<script>
import { fetchBusinessAnalyticsMonthly, fetchBusinessWalletPosition } from "@/services/businessAnalyticsService";
import AnalyticsChartCard from "./AnalyticsChartCard.vue";
import BusinessProfilesCard from "./BusinessProfilesCard.vue";
import SalesPipelineSection from "./SalesPipelineSection.vue";
import { lineChartOption, monthLabel, formatCount, formatMoney, BRAND, FAILED_RED } from "./chartTheme";

const DEFAULT_CURRENCY = "GBP";

function errorMessage(err, fallback) {
  if (err && err.code === "ECONNABORTED") {
    return "This took too long. The wallet service may be slow or unreachable.";
  }
  const status = err && err.response && err.response.status;
  if (status === 403) return "You don't have permission to view Business Analytics.";
  return (err && err.response && err.response.data && err.response.data.message) || fallback;
}

/** Every currency code that appears in any of the given per-currency amount maps, sorted. */
function currenciesIn(maps) {
  const codes = new Set();
  maps.forEach((m) => Object.keys(m || {}).forEach((c) => codes.add(c)));
  return [...codes].sort();
}

/**
 * Builds the chart option and its table view from the same per-month values, so the two never disagree.
 * @param {Array} months
 * @param {Array<{ name, values: Array<number|null>, emphasis?, color? }>} series
 * @param {(v: number) => string} format
 * @param {Object} [extra]  { approximate: boolean[], approximateLabel, integer, fitToData }
 */
function buildChart(months, series, format, extra = {}) {
  if (!months.length) return { option: null, table: { columns: [], rows: [] } };
  const option = lineChartOption({
    months,
    series: series.map((s) => ({ name: s.name, data: s.values, emphasis: s.emphasis, color: s.color })),
    format,
    ...extra,
  });
  const approximate = extra.approximate || [];
  const rows = months.map((m, i) => {
    const notes = [];
    if (m.partial) notes.push("so far");
    if (approximate[i]) notes.push((extra.approximateLabel || "Approx.").toLowerCase());
    return {
      label: monthLabel(m.month),
      note: notes.join(", "),
      cells: series.map((s) => (s.values[i] == null ? "–" : format(s.values[i]))),
    };
  });
  return { option, table: { columns: series.map((s) => s.name), rows } };
}

/** A small currency dropdown for one chart. */
const CurrencySelect = {
  name: "CurrencySelect",
  props: {
    value: { type: String, default: "" },
    options: { type: Array, required: true },
    label: { type: String, required: true },
  },
  render(h) {
    return h("b-form-select", {
      class: "ba-currency-select",
      props: { value: this.value, options: this.options, size: "sm" },
      attrs: { "aria-label": `${this.label} currency` },
      on: { input: (v) => this.$emit("input", v) },
    });
  },
};

export default {
  name: "BusinessAnalyticsComponent",
  components: {
    "bread-crumb": () => import("@/components/BreadCrumb.vue"),
    "analytics-chart-card": AnalyticsChartCard,
    "currency-select": CurrencySelect,
    "business-profiles-card": BusinessProfilesCard,
    "sales-pipeline-section": SalesPipelineSection,
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Business", links: "/business/list" },
        { title: "Business Analytics", links: "" },
      ],
      // Each money chart has its own currency; empty means "the default for that chart".
      selected: { volume: "", flow: "", balance: "", position: "" },
      monthly: null,
      monthlyLoading: false,
      monthlyError: "",
      position: null,
      positionLoading: false,
      positionError: "",
    };
  },
  computed: {
    months() {
      return (this.monthly && this.monthly.months) || [];
    },
    approvedBeforeTracking() {
      return (this.monthly && this.monthly.approvedBeforeTracking) || 0;
    },
    /** The currencies each money chart actually has data in. */
    currencyOptions() {
      const m = this.months;
      return {
        volume: currenciesIn(m.flatMap((x) => [x.businessVolume.outgoing, x.businessVolume.incoming])),
        flow: currenciesIn(m.flatMap((x) => [x.walletFunding, x.walletWithdrawal])),
        balance: currenciesIn(m.map((x) => x.walletBalanceTrend && x.walletBalanceTrend.byCurrency)),
        position: ((this.position && this.position.currencies) || []).map((c) => c.currency).sort(),
      };
    },
    onboardingChart() {
      return buildChart(
        this.months,
        [
          // Approved is green on both Business charts; Registered takes navy.
          { name: "Registered", values: this.months.map((m) => m.registeredBusinesses), color: BRAND.navy },
          { name: "Approved", values: this.months.map((m) => m.approvedBusinesses), color: BRAND.green },
        ],
        formatCount,
        { integer: true }
      );
    },
    monthEndChart() {
      return buildChart(
        this.months,
        [
          { name: "Approved", values: this.months.map((m) => m.monthEndBusinessCount.approved), color: BRAND.green },
          { name: "Pending", values: this.months.map((m) => m.monthEndBusinessCount.pending), color: BRAND.navy },
        ],
        formatCount,
        // Running totals that never drop near zero: fit the axis to the data rather than starting at 0.
        { integer: true, fitToData: true }
      );
    },
    statusChart() {
      const t = (key) => this.months.map((m) => m.transactionStatusTrend[key]);
      // Order sets the colour (logo green, navy, dark green, slate); Failed is always red.
      return buildChart(
        this.months,
        [
          { name: "Completed", values: t("completed"), emphasis: true },
          { name: "Submitted", values: t("submitted") },
          { name: "Cancelled", values: t("cancelled") },
          { name: "Refunded", values: t("refunded") },
          { name: "Failed", values: t("failed"), color: FAILED_RED },
        ],
        formatCount,
        { integer: true }
      );
    },
    volumeChart() {
      const currency = this.currencyFor("volume");
      const v = (line) => this.months.map((m) => this.amountIn(m.businessVolume[line], currency));
      return buildChart(
        this.months,
        [
          { name: "Outgoing", values: v("outgoing") },
          { name: "Incoming", values: v("incoming") },
        ],
        (value) => formatMoney(value, currency)
      );
    },
    walletFlowChart() {
      const currency = this.currencyFor("flow");
      return buildChart(
        this.months,
        [
          { name: "Deposits", values: this.months.map((m) => this.amountIn(m.walletFunding, currency)) },
          { name: "Withdrawals", values: this.months.map((m) => this.amountIn(m.walletWithdrawal, currency)) },
        ],
        (value) => formatMoney(value, currency)
      );
    },
    balanceChart() {
      const firstTracked = this.months.findIndex((m) => m.walletBalanceTrend);
      if (firstTracked === -1) return { option: null, table: { columns: [], rows: [] } };
      const currency = this.currencyFor("balance");
      // Only months from the first snapshot on; earlier months have no balance, not a zero balance.
      const months = this.months.slice(firstTracked);
      const values = months.map((m) => {
        const snapshot = m.walletBalanceTrend;
        if (!snapshot) return null;
        const b = snapshot.byCurrency[currency];
        return b ? b.available + b.pending + b.hold : 0;
      });
      const incomplete = months.map((m) => Boolean(m.walletBalanceTrend && !m.walletBalanceTrend.complete));
      return {
        ...buildChart(months, [{ name: "Month-end balance", values }], (value) => formatMoney(value, currency), {
          approximate: incomplete,
          approximateLabel: "Incomplete",
        }),
        trackingStarted: monthLabel(months[0].month),
        hasIncomplete: incomplete.some(Boolean),
      };
    },
    positionForCurrency() {
      if (!this.position) return null;
      const currency = this.currencyFor("position");
      return this.position.currencies.find((c) => c.currency === currency) || null;
    },
    positionTiles() {
      const p = this.positionForCurrency;
      if (!p) return [];
      const money = (value) => formatMoney(value, p.currency);
      return [
        { label: "Available", value: money(p.available) },
        { label: "Pending", value: money(p.pending) },
        { label: "On hold", value: money(p.hold) },
        { label: "Wallets", value: formatCount(p.walletCount) },
      ];
    },
  },
  created() {
    this.loadMonthly();
    this.loadPosition();
  },
  methods: {
    formatCount,
    /** The chart's chosen currency, or GBP, or the first currency it has data in. */
    currencyFor(chart) {
      const options = this.currencyOptions[chart];
      if (options.includes(this.selected[chart])) return this.selected[chart];
      if (!options.length || options.includes(DEFAULT_CURRENCY)) return DEFAULT_CURRENCY;
      return options[0];
    },
    /** A per-currency amount map → one currency's amount; absent means none that month. */
    amountIn(byCurrency, currency) {
      return (byCurrency && byCurrency[currency]) || 0;
    },
    formatDateTime(iso) {
      return iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "";
    },
    async loadMonthly() {
      this.monthlyLoading = true;
      this.monthlyError = "";
      try {
        this.monthly = await fetchBusinessAnalyticsMonthly();
      } catch (err) {
        this.monthlyError = errorMessage(err, "Couldn't load Business Analytics. Please try again.");
      } finally {
        this.monthlyLoading = false;
      }
    },
    async loadPosition() {
      this.positionLoading = true;
      this.positionError = "";
      try {
        this.position = await fetchBusinessWalletPosition();
      } catch (err) {
        this.positionError = errorMessage(err, "Couldn't load the Wallet Position. Please try again.");
      } finally {
        this.positionLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Cards stretch to fill the width: two per row on a wide screen, one on a narrow one. */
.ba-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 520px), 1fr));
  gap: 24px;
  margin-bottom: 24px;
}
.ba-section-title {
  font-size: 18px;
  font-weight: 600;
  margin: 8px 0 12px;
}
.ba-currency-select {
  width: auto;
  min-width: 84px;
}
/* Wallet Position uses the same rounded frame and theme-coloured title bar as the chart cards. */
.ba-position {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(33, 47, 63, 0.12);
  margin-bottom: 24px;
}
.ba-position-header {
  height: 52px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
}
.ba-position-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: inherit;
}
.ba-position-body {
  padding: 16px;
}
.ba-position-state {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  opacity: 0.8;
}
.ba-state-error {
  color: #c0392b;
  opacity: 1;
}
.ba-tile {
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-radius: 8px;
  padding: 12px;
}
.ba-tile-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.75;
}
.ba-tile-value {
  font-size: 20px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.ba-position-meta {
  font-size: 13px;
  opacity: 0.85;
}
.ba-position-warning {
  color: #b9770e;
}
</style>
